import {StoreConfiguration} from "@/utilities/API";
import isEqual from "lodash/isEqual";
import mapValues from "lodash/mapValues";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import { materialProduct } from "@/types/product";
import Length from "../../../../source/js/shared/models/Geometry/Length";

export default {
	state: (): object => ( {
		isGeneratingShareLink: false,
		shortLink: '',
		lastConfiguration: {},
		currentProductThickness: window.measurementSelectorVariables.thickness,
		currentProductMaterial: window.measurementSelectorVariables.material,
		alternateThicknesses: window.measurementSelectorVariables.alternateThicknesses,
		alternateMaterials: window.measurementSelectorVariables.alternateMaterials,
		configurationName: '',
	} ),
	mutations: {
		updateIsGenerating: ( state, value: boolean ): boolean => state.isGeneratingShareLink = value,
		updateShortLink: ( state, value: string ): string => state.shortLink = value,
		updateLastConfiguration: ( state, value: object ): object => state.lastConfiguration = value,
		updateThickness: ( state, thickness: number ): number => state.currentProductThickness = thickness,
		updateMaterial: ( state, material: object ): object => state.currentProductMaterial = material,
		updateConfigurationName: ( state, name: string ): string => state.configurationName = name,
	},
	getters: {
		getConfigurationName: ( state ): string => state.configurationName,
		measurementsConfig: ( state, getters ): object => {
			const config = {
				base: {
					shape: getters.shape,
					presetShape: getters.presetShape,
					primitives: getters.primitives,
					edgeFinish: getters.activeEdgeFinish,
					originalUpload: btoa(getters.uploadedContent),
					originalFilename: getters.getOriginalFilename,
					originalFileType: getters.getOriginalFileType,
				},
				drillholes: getters.drillHoleConfig,
				cutout: getters.cutoutConfig,
				cutouts: getters.cutoutsConfig,
				thickness: {
					value: getters.currentProductThickness.thickness,
				},
				version: 1
			};

			if ( getters.dxfDefinition ) {
				config.base[ 'dxfDefinition' ] = getters.dxfDefinition;
			}

			return config;
		},
		stringifiedConfig: ( state, getters ): string => {
			return JSON.stringify( getters.measurementsConfig );
		},
		encodedConfiguration: ( state, getters ): string => {
			return btoa( encodeURIComponent( getters.stringifiedConfig ) );
		},
		shareableMeasurementsConfig: ( state, getters ): string => {
			return `${window.location.href.split( '?' )[ 0 ]}?share_configuration=${getters.encodedConfiguration}`;
		},
		isGeneratingLink: ( state ): boolean => {
			return state.isGeneratingShareLink;
		},
		lastGeneratedConfiguration: ( state ): object => {
			return state.lastConfiguration;
		},
		shortLink: ( state ): string => state.shortLink,
		alternateThicknesses: ( state ): string => state.alternateThicknesses,
		alternateMaterials: ( state, getters ): materialProduct[] => {
			if(getters.getBlockSettings) {
				return getters.getBlockSettings.alternateMaterials;
			}

			return state.alternateMaterials;
		},
		currentProductThickness: ( state ): string => state.currentProductThickness,
		currentProductMaterial: ( state ): string => state.currentProductMaterial,
		hasAlteredConfiguration: ( state, getters ): boolean => {
			return isEqual( getters.measurementsConfig, getters.lastGeneratedConfiguration ) === false;
		},
		getBlockConfig: (): object|null => {
			const block = document.querySelector('#measurements-selector-block');
			if(!block) return null;

			const configData = block.getAttribute('data-config');

			if(!configData) return null;

			const config = JSON.parse(configData);

			if(config.length === 0) return null;

			return config[0].configuration;
		},
		getBlockSettings: (): object|null => {
			const block = document.querySelector('#measurements-selector-block');
			if(!block) return null;

			const settingData = block.getAttribute('data-settings');
			return JSON.parse(settingData);
		},
		isCutoutsEnabled: ( state, getters ): boolean => {
			if(getters.getBlockSettings?.disable_cutouts) return false;

			return getters.getPricingGroup.getEnableCutouts();
		},
		isDrillHolesEnabled: ( state, getters ): boolean => {
			if(getters.getBlockSettings?.disable_drillholes) return false;

			return getters.getPricingGroup.getEnableDrillholes();
		},
		isDrillHolesVisible: ( state, getters ): boolean => {
			if(!getters.shape) return false;

			return getters.isDrillHolesEnabled && getters.shape !== 'dxf' && getters.shape !== 'text';
		}
	},
	actions: {
		async generateConfigurationLink( { getters }, { url, isTemporary = false } ): Promise<string> {
			const configuration = getters.measurementsConfig;
			const lastConfiguration = getters.lastGeneratedConfiguration;

			// If nothing changed, skip generating a new shortlink.
			if ( isEqual( configuration, lastConfiguration ) && ( getters.shortLink !== '' && getters.shortLink !== undefined ) ) {
				return '';
			}

			const action = 'save';
			const name = getters.getConfigurationName;

			const request = await StoreConfiguration( url, configuration, isTemporary, action, name );

			if ( ! isEmpty( request.data.short_link ) && request.data.short_link !== '' ) {
				return request.data.short_link;
			}

			return '';
		},
		async createConfigurationLink( { commit, dispatch, getters } ): Promise<void> {
			commit( 'updateIsGenerating', true );

			const { material } = window.measurementSelectorVariables;
			const result = await dispatch( 'generateConfigurationLink', {
				url: material.permalink.split( '?' )[ 0 ],
				isTemporary: false,
			});

			if ( result !== '' ) {
				commit( 'updateShortLink', result );
				commit( 'updateLastConfiguration', getters.measurementsConfig );
			}

			commit( 'updateIsGenerating', false );
		},
		async generateShareableLink( { getters }, { url, isTemporary = false } ): Promise<string> {
			const configuration = getters.measurementsConfig;
			const lastConfiguration = getters.lastGeneratedConfiguration;

			// If nothing changed, skip generating a new shortlink.
			if ( isEqual( configuration, lastConfiguration ) && ( getters.shortLink !== '' && getters.shortLink !== undefined ) ) {
				return '';
			}

			const request = await StoreConfiguration( url, configuration, isTemporary, 'share', '' );

			if ( ! isEmpty( request.data.short_link ) && request.data.short_link !== '' ) {
				return request.data.short_link;
			}

			return '';
		},
		async createShareableLink( { commit, dispatch, getters } ): Promise<void> {
			commit( 'updateIsGenerating', true );

			const { material } = window.measurementSelectorVariables;
			const result = await dispatch( 'generateShareableLink', {
				url: material.permalink.split( '?' )[ 0 ],
				isTemporary: false,
			});

			if ( result !== '' ) {
				commit( 'updateShortLink', result );
				commit( 'updateLastConfiguration', getters.measurementsConfig );
			}

			commit( 'updateIsGenerating', false );
		},
		async loadConfiguration( { commit, dispatch, getters } ): Promise<void> {
			const { measurementsConfig, currentShortLinkUrl } = window.measurementSelectorVariables;
			let currentConfig = measurementsConfig;

			// Load the gutenberg block configuration if available.
			currentConfig = getters.getBlockConfig ? getters.getBlockConfig : currentConfig;

			// Remove share_configuration from URL.
			if ( window.history.replaceState && ( window.location.href.indexOf( 'share_configuration' ) !== -1 || window.location.href.indexOf( 'share=t_' ) !== -1 ) ) {
				window.history.replaceState( {}, '', window.location.origin + window.location.pathname );
			}

			// Possible load prefilled drillholes from measurementSelectorVariables
			dispatch( 'updatePrefilledDrillHoles' );

			//If the configuration is empty, do not proceed.
			if( !currentConfig || isEmpty( currentConfig ) || currentConfig.length === 0 ) {
				return;
			}

			commit( 'updateShortLink', currentShortLinkUrl );
			commit( 'updateLastConfiguration', currentConfig );
			dispatch( 'updateFromConfig', currentConfig );
		},
		async updatePrefilledDrillHoles( { dispatch, getters } ): Promise<void> {
			const { other_settings } = window.measurementSelectorVariables.pricingGroup;
			const { prefilledDrillHoles } = window.measurementSelectorVariables.drillHoleSettings;

			if ( getters.isDrillHolesEnabled && prefilledDrillHoles.length > 0 ) {

				// Map values to be ints
				const mappedPrefilledDrillHoles = map( prefilledDrillHoles, ( drillHole: object ) => {
					return mapValues( drillHole, ( val: string ) => parseInt( val ) );
				} );

				await dispatch( 'updateDrillHolesFromConfig', { type: 'custom', settings: mappedPrefilledDrillHoles, diameter: other_settings.default_drillhole_diameter } );
			}
		},
		async updateBaseFromConfig( { commit, dispatch }, base ): Promise<void> {
			const { shape, primitives, presetShape } = base;

			if ( shape === 'dxf' ) {
				commit( 'setUploadedContent', { result: atob(base.originalUpload), filename: base.originalFilename, filetype: base.originalFileType } );
			}

			if ( shape === 'text' ) {
				commit( 'updateModifier', { modifier: 'desired_dimension', value: primitives.desired_dimension } );
				commit( 'updateModifier', { modifier: 'font', value: primitives.font } );
				commit( 'updateModifier', { modifier: 'text', value: primitives.text } );
			}

			if ( shape === 'rounded-rectangle' ) {
				commit( 'updateModifier', { modifier: 'use_individual_corners', value: primitives.use_individual_corners } );
			}

			if ( shape === 'polygon' ) {
				commit( 'updateModifier', { modifier: 'side_amount', value: primitives.side_amount } );
			}

			if ( shape === 'star' ) {
				commit( 'updateModifier', { modifier: 'number_of_points', value: primitives.number_of_points } );
			}

			// Convert the numerical dimensions to strings. Non-numerical values are set to undefined.
			let dimensions = mapValues( primitives, ( primitive ) => {
				if ( typeof primitive !== 'number' ) {
					return;
				}

				return primitive.toString();
			} );

			// Any value that is considered undefined is removed.
			dimensions = omitBy( dimensions, isUndefined );

			if ( presetShape == '' && (shape === 'rectangle' || shape === 'text' || shape === 'dxf') ) {
				commit( 'updateMenuItem', shape );
			} else {
				commit( 'updateMenuItem', 'shapes' );
			}
			await dispatch( 'updateShape', shape );
			commit( 'updatePresetShape', presetShape );

			commit( 'setDimensionsForShape', { shape, dimensions } );
		},
		async updateFromConfig( { dispatch, getters, commit }, { base, cutout, drillholes, cutouts } ): Promise<void> {
			const { enabled_shapes } = window.measurementSelectorVariables.pricingGroup;
			const edgeFinishingIsEnabled  = window.measurementSelectorVariables.edgeFinishingIsEnabled === '1';

			if ( base && base.shape && [ 'rectangle', ...enabled_shapes ].includes( base.shape ) ) {
				await dispatch( 'updateBaseFromConfig', base );
				commit( 'updateUsingDefaultDimensions', false );
			}

			// Set cutout if enabled and the shape is included in the list of enabled shapes.
			if ( getters.isCutoutsEnabled && cutout && cutout.type !== 'none' && !cutouts ) {

				// Fix for old configs with circles, the diameter needs to be in cm.
				// Now it will work in the new config with multiple cutouts.
				if ( cutout.shape === 'circle' ) {
					const diameterInCm = Length.from_mm(cutout.primitives.diameter).in_cm();
					cutout.primitives.diameter = diameterInCm;
				}

				cutouts = [];
				cutouts.push( cutout );
			}

			if(getters.isCutoutsEnabled && cutouts && cutouts.length > 0) {
				await dispatch( 'updateCutoutsFromConfig', cutouts );
			} else {
				await dispatch( 'resetCutouts' );
			}

			if ( getters.isDrillHolesEnabled && drillholes !== undefined ) {
				await dispatch( 'updateDrillHolesFromConfig', drillholes );
			}

			if( edgeFinishingIsEnabled && base && base.edgeFinish ) {
				commit( 'updateActiveEdgeFinish', base.edgeFinish );
			}
		},
	}
};
